import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Briefcase, 
  Eye, 
  MessageSquare, 
  Search, 
  Plus, 
  MapPin, 
  Clock, 
  HardHat, 
  Settings, 
  Folder, // Icon for Projects
  Wrench  // Icon for Odd Jobs
} from "lucide-react";
import styles from "./BusinessDashboard.module.css";

const BusinessDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('leads'); // 'leads' or 'work'

  const stats = { leads: 12, activeQuotes: 5, jobsWon: 3, profileViews: 156 };

  // 1. INCOMING LEADS (External)
  const jobLeads = [
    { id: 1, title: "Kitchen Renovation", location: "Sydney CBD", budget: "$15k - $20k", posted: "2h ago", urgent: true },
    { id: 2, title: "Bathroom Tiling", location: "Parramatta", budget: "$3k - $5k", posted: "5h ago", urgent: false },
  ];

  // 2. ACTIVE WORK (Internal - Both Projects & Jobs)
  const activeWorkData = [
    { 
      id: 101, 
      type: 'project', 
      title: "Smith Family Extension", 
      stage: "Framing", 
      tasksRemaining: 4, 
      nextDeadline: "Fri 12th" 
    },
    { 
      id: 201, 
      type: 'job', // Odd Job
      title: "Fix Leaking Roof", 
      status: "In Progress", 
      client: "Mrs. Jones",
      nextDeadline: "Tomorrow" 
    },
    { 
      id: 102, 
      type: 'project', 
      title: "Unit 404 Reno", 
      stage: "Planning", 
      tasksRemaining: 1, 
      nextDeadline: "Mon 15th" 
    },
    { 
      id: 202, 
      type: 'job', // Odd Job
      title: "Replace Circuit Breaker", 
      status: "Scheduled", 
      client: "BuildCorp",
      nextDeadline: "Wed 17th" 
    },
  ];

  return (
    <div className={styles.dashboardContainer}>
      <header className={styles.header}>
        <div className={styles.container} style={{paddingTop:'1rem', paddingBottom:'1rem'}}>
          <div className={styles.headerContent}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
               <HardHat size={32} color="#F59E0B" />
               <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>BuildStart</span>
            </div>
            <div style={{color: 'white'}}>John's Construction</div>
          </div>
        </div>
      </header>

      <div className={styles.container}>
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#0F172A', marginBottom: '0.5rem' }}>
            Welcome back, John! 👷‍♂️
          </h1>
          <p style={{ color: '#64748B' }}>Here is your business overview for today.</p>
        </div>

        {/* --- STATS STRIP --- */}
        <div className={styles.statsGrid}>
          {/* ... (Keep stats cards the same as previous) ... */}
           <div className={styles.slimStatCard}>
            <div className={styles.statInfo}>
              <h3>New Leads</h3>
              <p>{stats.leads}</p>
            </div>
            <div className={styles.statIconBox}><Search size={20} /></div>
          </div>
          <div className={styles.slimStatCard}>
            <div className={styles.statInfo}>
              <h3>Active Quotes</h3>
              <p>{stats.activeQuotes}</p>
            </div>
            <div className={styles.statIconBox}><MessageSquare size={20} /></div>
          </div>
          <div className={styles.slimStatCard}>
            <div className={styles.statInfo}>
              <h3>Jobs Won</h3>
              <p>{stats.jobsWon}</p>
            </div>
            <div className={styles.statIconBox}><Briefcase size={20} /></div>
          </div>
          <div className={styles.slimStatCard}>
            <div className={styles.statInfo}>
              <h3>Profile Views</h3>
              <p>{stats.profileViews}</p>
            </div>
            <div className={styles.statIconBox}><Eye size={20} /></div>
          </div>
        </div>

        <div className={styles.mainLayoutGrid}>
          
          {/* LEFT COLUMN: MAIN TABS */}
          <div className={styles.leftCol}>
            <div className={styles.tabContainer}>
              <div className={styles.tabHeader}>
                <button 
                  className={`${styles.tabButton} ${activeTab === 'leads' ? styles.activeTab : ''}`}
                  onClick={() => setActiveTab('leads')}
                >
                  Job Leads (2 New)
                </button>
                <button 
                  className={`${styles.tabButton} ${activeTab === 'work' ? styles.activeTab : ''}`}
                  onClick={() => setActiveTab('work')}
                >
                  My Active Work ({activeWorkData.length})
                </button>
              </div>

              <div className={styles.tabContent}>
                
                {/* 1. JOB LEADS TAB */}
                {activeTab === 'leads' && (
                  <>
                    {jobLeads.map(lead => (
                      <div key={lead.id} className={styles.listItem}>
                        <div className={styles.itemMain}>
                          <h4>
                            {lead.title} 
                            {lead.urgent && <span className={styles.urgentBadge}>URGENT</span>}
                          </h4>
                          <div className={styles.itemMeta}>
                            <span><MapPin size={14}/> {lead.location}</span>
                            <span><Clock size={14}/> {lead.posted}</span>
                          </div>
                        </div>
                        <div style={{textAlign: 'right'}}>
                          <div className={styles.priceTag}>{lead.budget}</div>
                          <button className={styles.btnView} style={{marginTop: '0.5rem'}}>View Details</button>
                        </div>
                      </div>
                    ))}
                    <a href="/business-leads" className={styles.viewAllLink}>View All Job Leads →</a>
                  </>
                )}

                {/* 2. ACTIVE WORK TAB (Projects + Jobs) */}
                {activeTab === 'work' && (
                  <div className={styles.workListContainer}>
                    {activeWorkData.map(item => (
                      <div key={item.id} className={`${styles.listItem} ${styles.workCard}`}>
                        
                        {/* ICON INDICATOR */}
                        <div className={`${styles.workIcon} ${item.type === 'project' ? styles.iconProject : styles.iconJob}`}>
                          {item.type === 'project' ? <Folder size={20} /> : <Wrench size={20} />}
                        </div>

                        {/* MAIN INFO */}
                        <div className={styles.itemMain}>
                          <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                            <h4>{item.title}</h4>
                            <span className={styles.typeLabel}>{item.type === 'project' ? 'PROJECT' : 'JOB'}</span>
                          </div>
                          
                          <div className={styles.itemMeta}>
                            {/* Render different details based on type */}
                            {item.type === 'project' ? (
                              <>
                                <span style={{color: '#D97706', fontWeight: 600}}>Stage: {item.stage}</span>
                                <span>• {item.tasksRemaining} Tasks Active</span>
                              </>
                            ) : (
                              <>
                                <span style={{color: '#16A34A', fontWeight: 600}}>{item.status}</span>
                                <span>• Client: {item.client}</span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* ACTION SIDE */}
                        <div style={{textAlign: 'right'}}>
                          <div style={{fontSize: '0.85rem', color: '#64748B'}}>Due: {item.nextDeadline}</div>
                          <button className={styles.btnView} style={{marginTop: '0.5rem'}}>
                            {item.type === 'project' ? 'Manage' : 'Update'}
                          </button>
                        </div>

                      </div>
                    ))}
                    <div style={{textAlign:'center', marginTop: '2rem', color: '#64748B', fontSize: '0.9rem'}}>
                      Manage all your active work here.
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: SIDEBAR */}
          <div className={styles.sidebar}>
            
            {/* Quick Actions - UPDATED */}
            <div className={styles.sidebarCard}>
              <div className={styles.sidebarHeader}>
                <HardHat size={18} color="#D97706" /> Quick Actions
              </div>
              
              <button className={`${styles.actionBtn} ${styles.btnPrimary}`}>
                <Plus size={18} /> Create New Project
              </button>
              
              {/* Added Create Job Button */}
              <button className={`${styles.actionBtn} ${styles.btnSecondary}`}>
                <Wrench size={18} /> Add Odd Job
              </button>
              
              <button className={`${styles.actionBtn} ${styles.btnSecondary}`}>
                <Plus size={18} /> Post Sub-contract Job
              </button>
            </div>

            {/* Profile Health */}
            <div className={styles.sidebarCard}>
              <div className={styles.sidebarHeader}>
                <Settings size={18} color="#0F172A" /> Profile Strength
              </div>
              <div className={styles.progressContainer}>
                <div className={styles.progressHeader}>
                  <span>Completion</span>
                  <span style={{fontWeight:'bold'}}>85%</span>
                </div>
                <div className={styles.progressBarBg}>
                  <div className={styles.progressBarFill} style={{width: '85%'}}></div>
                </div>
              </div>
              {/* ... checklist ... */}
              <button className={styles.viewAllLink} style={{textAlign:'left'}}>Complete Profile →</button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BusinessDashboard;